import { useEffect, useState, FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Building2, CheckCircle2, MailCheck } from 'lucide-react';

import { authService } from '../services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const GENERIC_ERROR = 'El código o enlace es inválido o expirado. Solicita uno nuevo desde tu cuenta.';

/** Página /verificar (auth 007 FR-014): procesa el link del correo (token,
    anónimo) y acepta el código de 6 dígitos tecleado (requiere sesión). */
function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const hasSession = !!localStorage.getItem('access_token');

  const [status, setStatus] = useState<'idle' | 'working' | 'done' | 'error'>(
    token ? 'working' : 'idle'
  );
  const [error, setError] = useState('');
  const [code, setCode] = useState('');

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    authService
      .verifyEmail({ token })
      .then(() => {
        if (!cancelled) setStatus('done');
      })
      .catch(() => {
        if (!cancelled) {
          setStatus('error');
          setError(GENERIC_ERROR);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleCodeSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setStatus('working');
    try {
      await authService.verifyEmail({ code: code.trim() });
      setStatus('done');
    } catch {
      setStatus('error');
      setError(GENERIC_ERROR);
    }
  };

  return (
    <div className="min-h-[100dvh] flex flex-col items-center justify-center bg-background px-6">
      <div className="mb-10 flex items-center gap-2">
        <Building2 className="h-5 w-5 text-primary" />
        <span className="font-semibold tracking-tight">Takarah</span>
      </div>

      <div className="w-full max-w-sm text-center">
        {status === 'done' ? (
          <>
            <CheckCircle2 className="mx-auto mb-4 h-10 w-10 text-emerald-600" />
            <h1 className="mb-2 text-2xl font-semibold tracking-tight">Correo verificado</h1>
            <p className="mb-8 text-sm text-muted-foreground">
              Tu cuenta quedó activa por completo. Ya puedes publicar propiedades y gestionar
              rentas.
            </p>
            <Button asChild className="w-full">
              <Link to={hasSession ? '/properties' : '/login'}>
                {hasSession ? 'Ir a mis propiedades' : 'Inicia sesión'}
              </Link>
            </Button>
          </>
        ) : (
          <>
            <MailCheck className="mx-auto mb-4 h-10 w-10 text-primary" />
            <h1 className="mb-2 text-2xl font-semibold tracking-tight">Verifica tu correo</h1>

            {token && status === 'working' && (
              <p className="text-sm text-muted-foreground">Verificando tu enlace…</p>
            )}

            {error && <p className="mb-6 text-sm text-destructive">{error}</p>}

            {!token && !hasSession ? (
              <p className="text-sm text-muted-foreground">
                Abre el enlace del correo que te enviamos, o{' '}
                <Link to="/login" className="font-medium text-foreground underline">
                  inicia sesión
                </Link>{' '}
                para verificar con tu código de 6 dígitos.
              </p>
            ) : (
              (!token || status === 'error') && (
                <form onSubmit={handleCodeSubmit} className="space-y-5 text-left">
                  <div className="space-y-1.5">
                    <Label htmlFor="code">Código de 6 dígitos</Label>
                    <Input
                      id="code"
                      inputMode="numeric"
                      pattern="[0-9]{6}"
                      maxLength={6}
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder="000000"
                      required
                      disabled={!hasSession}
                    />
                    {!hasSession && (
                      <p className="text-xs text-muted-foreground">
                        Para usar el código necesitas{' '}
                        <Link to="/login" className="underline">
                          iniciar sesión
                        </Link>
                        .
                      </p>
                    )}
                  </div>
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={!hasSession || status === 'working'}
                  >
                    {status === 'working' ? 'Verificando…' : 'Verificar'}
                  </Button>
                </form>
              )
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default VerifyEmailPage;
